import React, { useContext, useEffect, useMemo, useState } from 'react';
import withDataLoaded from '../with_data_loaded';
import PageSpinner from '../shared/page_spinner';
import {
  companyDetailsLoader,
  companyElementTypesLoader,
  pricingProgramNamesLoader,
  userLoader,
} from '../../actions/data_loaders';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';

import { useSelector } from 'react-redux';
import Actions from '../../actions/actions';
import FunctionalityAccessHelper from '../../helpers/functionality_access_helper';
import { ReactComponent as Sun } from '../../images/sun.svg';
import { TabSwapWarningContext } from './components/tab_swap_warning_context';
import email_helper from '../../helpers/email_helper';
import TitledDivider from './components/titled_divider';
import PhoneFormatInput, { ExtensionFormatInput } from '../shared/phone_format_input';
import Constants from '../../helpers/constants';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import CompanyElementForm from './company_details_element_form';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';

const DialogBox = (props) => {
  return (
    <Dialog id={'qa-company-edit-nav-modal'} open={props.open}>
      <DialogTitle>You have unsaved changes. Do you want to save changes?</DialogTitle>

      <DialogActions>
        <Button onClick={props.onCancel} id={'qa-company-edit-nav-modal-discard'}>
          No, Discard Changes
        </Button>
        <Button
          id="qa-company-edit-nav-modal-save"
          onClick={props.onAgree}
          variant="contained"
          color="primary"
          autoFocus
        >
          Yes, Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CancelDialogBox = (props) => {
  return (
    <Dialog id={'qa-company-edit-cancel-modal'} open={props.open}>
      <DialogTitle>Are you sure you want to cancel changes?</DialogTitle>

      <DialogActions>
        <Button onClick={props.onCancel} id={'qa-company-edit-cancel-modal-keep'}>
          No, Continue Editing
        </Button>
        <Button
          id="qa-company-edit-cancel-modal-discard"
          onClick={props.onAgree}
          variant="contained"
          color="primary"
          autoFocus
        >
          Yes, Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditCompany = () => {
  const currUser = useSelector((state) => state.user); //currently logged in user
  const currUserAcls = useSelector((redux) => redux.userFunctionalityAcls);
  const companyInfo = useSelector((state) => state.company);
  const products = companyInfo?.financingProducts;
  const pricingProgramNames = useSelector((state) =>
    state.pricingProgramNames?.reduce((acc, curr) => {
      const key = curr.code;
      const currentSkus = acc?.[key];
      const sku = curr.productSku;

      if (!currentSkus) return { ...acc, [key]: [sku] };
      return {
        ...acc,
        [key]: [...acc[key], sku],
      };
    }, {})
  );

  const view = useSelector((state) => state.view);
  const companyElementTypes = useSelector((state) => state?.companyElementTypes);
  const [editCompany, setEditCompany] = useState(companyInfo?.company);
  const { changeDialogContext, setChangeDialogContext } = useContext(TabSwapWarningContext);
  const initialSetOfFunctionalityAcls = companyInfo?.company?.functionalityAcls;
  let [enhancedRemoteQuickcheck, setEnhancedRemoteQuickcheck] = useState(
    initialSetOfFunctionalityAcls?.some(
      (element) => element?.featureCode === Constants.FUNCTIONALITY_ACL_FEATURE_CODE_ENHANCED_REMOTE_QUICK_CHECK
    )
  );

  const migrationProduct = Constants.LENDERS.find((lender) => 'SLOAN_MEDALLION_001' === lender?.productSku);
  const currentProducts = ['CASH_005', 'SLOAN_MEDALLION_001'];
  const onCurrentProduct = products?.some((fpa) => currentProducts.includes(fpa.productSku));
  const isCompanyDisabled = companyInfo?.financingProducts?.length === 0;

  let [isLoading, setIsLoading] = useState(false);
  let [waitingForChanges, setWaitingForChanges] = useState(false);

  let [companyTitleError, setCompanyTitleError] = useState(null);
  let [address1Error, setAddress1Error] = useState(null);
  let [zipError, setZipError] = useState(null);
  let [feinError, setFeinError] = useState(null);

  const isNew = !companyInfo?.company?.coid;

  const [editUser, setEditUser] = useState(companyInfo?.admin);
  const [enabledProducts, setEnabledProducts] = useState(
    companyInfo?.financingProducts?.map((product) => product.productSku) || ['SLOAN_MEDALLION_001']
  );
  let [firstnameError, setFirstnameError] = useState(null);
  let [lastnameError, setLastnameError] = useState(null);
  let [usernameError, setUsernameError] = useState(null);
  let [phoneError, setPhoneError] = useState(null);
  let [pricingTierError, setPricingTierError] = useState(null);
  let [productError, setProductError] = useState(null);

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const isCurrUserCPF = currUser.atype === 'CPF';

  const companyElementsToHide = [];
  if (isNew) {
    if (!enabledProducts.includes('CASH_005')) {
      companyElementsToHide.push('PAYMENT_PROGRAM_CASH');
    }
    // and a non cash product exists
    if (enabledProducts.filter((p) => p !== 'CASH_005')?.length === 0) {
      companyElementsToHide.push('PAYMENT_PROGRAM_LOAN');
    }
  }

  const startingElementForm = useMemo(
    () =>
      isNew
        ? companyElementTypes
            ?.filter((ce) => ce.isRequired)
            ?.reduce((acc, curr) => {
              return {
                ...acc,
                [curr.sku]: {
                  value: curr.defaultValue,
                  error: null,
                },
              };
            }, {})
        : companyInfo?.companyElements?.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.elementTypeSku]: {
                value: curr.elementValue,
                error: null,
              },
            }),
            {}
          ),
    [companyInfo, companyElementTypes, isNew]
  );

  const [elementsForm, setElementsForm] = useState(startingElementForm);
  useEffect(() => setElementsForm(startingElementForm), [startingElementForm]);

  /* eslint-disable  react-hooks/exhaustive-deps */
  useEffect(() => {
    const keys = [
      'name',
      'address1',
      'city',
      'state',
      'zip',
      'fein',
      'isTest',
      'trainingRequired',
      'mfaRequired',
      'pricingProgram',
      'functionalityAcls',
    ];
    if (companyInfo?.company?.coid) {
      if (isCompanyDisabled) {
        setChangeDialogContext({
          ...changeDialogContext,
          isChanges: false,
        });
        return;
      }

      setChangeDialogContext({
        ...changeDialogContext,
        isChanges:
          keys.some((a) => {
            if (a === 'isTest' || a === 'trainingRequired' || a === 'mfaRequired') {
              return !!editCompany?.[a] !== !!companyInfo?.company[a];
            }
            if (a === 'functionalityAcls') {
              return editCompany?.[a].length !== companyInfo?.company[a].length;
            }
            return (
              editCompany?.[a] !== companyInfo?.company[a] ||
              (!isNew && editCompany?.adminId && editCompany?.adminId !== companyInfo?.admin?.userid)
            );
          }) ||
          JSON.stringify(startingElementForm) !== JSON.stringify(elementsForm) ||
          !(
            enabledProducts?.sort()?.join(',') ===
            companyInfo?.financingProducts
              ?.map((product) => product.productSku)
              ?.sort()
              ?.join(',')
          ),
      });
    }
  }, [editCompany, companyInfo?.company, elementsForm, enabledProducts]);

  useEffect(() => {
    if (currUser !== null && !FunctionalityAccessHelper.hasAccessToAdmin(currUser)) {
      Actions.setErrorMessages(['Page not found']);
      Actions.setRoute('/proposal/new');
    }
    if (currUser.atype === 'A' && currUser.company.coid !== companyInfo?.company?.coid) {
      Actions.setErrorMessages(['Page not found']);
      Actions.setRoute('/proposal/new');
    }
    return () => Actions.clearUser();
  }, [currUser.atype]);

  useEffect(() => {
    if (editCompany?.zip.length === 5 && unnullify(editCompany?.state) !== '' && unnullify(editCompany?.city) !== '') {
      setWaitingForChanges(false);
    }
  }, [editCompany?.city]);

  /* eslint-enable  react-hooks/exhaustive-deps */

  function isQaaPricingProgram(pricingProgram) {
    return !!pricingProgram && pricingProgram.includes('QAA');
  }

  function unnullify(val) {
    return !val ? '' : val;
  }

  function goToCompanyList() {
    Actions.clearCompanyList();
    Actions.setRoute(`/account/companies`);
  }

  function onCancel() {
    if (isNew) {
      Actions.setRoute(`/account/companies/${view.coid}/company`);
      return;
    }
    if (changeDialogContext?.confirmTabSwap) {
      changeDialogContext.confirmTabSwap();
    }
  }

  const companyElementsValidation = () => {
    const validatedForm = Object.keys(elementsForm).reduce((acc, key) => {
      const field = elementsForm[key];
      return {
        ...acc,
        [key]: {
          ...field,
          error: field.isRequired && (!field.value || field.value === 'none') ? 'Please fill out' : null,
        },
      };
    }, {});

    setElementsForm(validatedForm);
    if (Object.keys(validatedForm).some((key) => validatedForm[key].error)) {
      Actions.setErrorMessages(['Please make sure all required fields are filled out']);
      return false;
    }

    const companyElements = Object.keys(elementsForm).map((key) => ({
      elementTypeSku: key,
      elementValue: elementsForm[key]?.value,
    }));

    const freezeFlag = companyElements.find((ec) => ec.elementTypeSku === 'M1_FREEZE_FLAG_LOAN');
    const freezeReason = companyElements.find((ec) => ec.elementTypeSku === 'M1_FREEZE_REASON_LOAN');

    if (freezeFlag?.elementValue !== 'true') {
      if (freezeReason) {
        freezeReason.elementValue = null;
      } else {
        companyElements.push({
          elementTypeSku: 'M1_FREEZE_REASON_LOAN',
          elementValue: null,
        });
      }
    } else {
      if (!freezeReason?.elementValue) {
        setElementsForm({
          ...elementsForm,
          M1_FREEZE_REASON_LOAN: {
            ...elementsForm['M1_FREEZE_REASON_LOAN'],
            error: 'Cannot be empty',
          },
        });
        Actions.setErrorMessages(['Freeze Reason must not be empty if Loan M1 Freeze checked']);
        return false;
      }
    }
    return companyElements;
  };

  async function onSave() {
    const { validCompany } = validateCompanyFields();
    const { validUser } = isNew ? validateUserFields() : { validUser: true };
    const valid = validCompany && validUser;

    if (!valid || waitingForChanges) {
      Actions.setErrorMessages(['Please enter required information']);
      return;
    }
    const companyElements = companyElementsValidation();
    if (!companyElements) return;

    setIsLoading(true);

    let response = isNew
      ? await Actions.createCompany({
          company: editCompany,
          admin: editUser,
          financingProducts: enabledProducts.map((p) => ({ productSku: p })),
        })
      : await Actions.updateCompany({
          companyData: {
            ...editCompany,
            companyElements,
            financingProductSkus: enabledProducts,
          },
          coid: view.coid,
        });
    await Actions.fetchCompanyAuditLogs(view.coid);
    if (!!response) {
      if (response.includes('Username already taken')) {
        Actions.setErrorMessages(['The email address you provided is already in use']);
        setUsernameError(`Email ${editUser?.username} is already taken`);
        setIsLoading(false);
        return;
      } else if (response.includes('fail')) {
        Actions.setErrorMessages(['Error creating/updating company']);
        setIsLoading(false);
        return;
      }
    }
    setIsLoading(false);
    isNew ? Actions.setMessages(['Company Added']) : Actions.setMessages(['Changes saved successfully']);

    if (changeDialogContext?.confirmTabSwap) {
      changeDialogContext.confirmTabSwap();
    }

    isNew && goToCompanyList();
  }

  function onBlurText({ currentTarget: { id: fieldname } }) {
    switch (fieldname) {
      case 'name':
        setCompanyTitleError(textIsInvalidCompany(fieldname));
        break;
      case 'address1':
        setAddress1Error(textIsInvalidCompany(fieldname));
        break;
      case 'zip':
        setZipError(textIsInvalidCompany(fieldname));
        break;
      case 'fein':
        setFeinError(textIsInvalidCompany(fieldname));
        break;
      case 'firstname':
        setFirstnameError(textIsInvalidUser(fieldname));
        break;
      case 'lastname':
        setLastnameError(textIsInvalidUser(fieldname));
        break;

      default:
        break;
    }
  }

  function onBlurPhone() {
    setPhoneError(phoneIsInvalid());
  }

  function onBlurUsername({ target: { value } }) {
    setUsernameError(validateUsername(value));
  }

  function onBlurPricingTier(value) {
    setPricingTierError(validatePricingTier(value.currentTarget.value));
  }

  function onBlurProduct(value) {
    setProductError(validateProduct(value.currentTarget.value));
  }

  function fieldNameToErrorTextName(fieldname) {
    switch (fieldname) {
      case 'name':
        return 'Name';
      case 'address1':
        return 'Address';
      case 'fein':
        return 'Ensemble View Alpha Id';
      case 'firstname':
        return 'First Name';
      case 'lastname':
        return 'Last Name';
      default:
        return fieldname;
    }
  }

  function textIsInvalidCompany(fieldName) {
    let value = editCompany?.[fieldName];
    let maxLength = 50;
    if (fieldName === 'address1') {
      maxLength = 35;
    } else if (fieldName === 'fein') {
      maxLength = 80;
    }

    let textError = false;
    if (value === '' || value === ' ' || value === undefined || value === null) {
      textError = 'Cannot be empty';
    } else if (value.length >= maxLength) {
      textError = `${fieldNameToErrorTextName(fieldName)} must be shorter than ${maxLength} characters`;
    } else if (fieldName === 'zip' && value.length !== 5) {
      textError = 'ZIP Code must be 5 digits';
    } else if (fieldName === 'zip' && (editCompany?.state === undefined || editCompany?.city === undefined)) {
      textError = 'Could not find ZIP Code';
    } else if (fieldName === 'fein' && value.length < 3) {
      textError = 'FEIN must be at least 3 characters long';
    }

    return textError;
  }

  function textIsInvalidUser(fieldName) {
    let value = editUser[fieldName];
    let maxLength = 50;
    if (fieldName === 'firstname' || fieldName === 'lastname') {
      maxLength = 35;
    } else if (fieldName === 'username') {
      maxLength = 45;
    }

    let textError = false;
    if (value === '' || value === ' ' || value === undefined || value === null) {
      textError = 'Cannot be empty';
    } else if (value.length >= maxLength) {
      textError = `${fieldNameToErrorTextName(fieldName)} must be shorter than ${maxLength} characters`;
    }
    return textError;
  }

  function validateCompanyFields() {
    const tmpCompanyTitleError = textIsInvalidCompany('name');
    const tmpAddress1Error = textIsInvalidCompany('address1');
    const tmpZipError = textIsInvalidCompany('zip');
    const tmpFeinError = textIsInvalidCompany('fein');

    setCompanyTitleError(tmpCompanyTitleError);
    setAddress1Error(tmpAddress1Error);
    setZipError(tmpZipError);
    setFeinError(tmpFeinError);

    let anErrorOccurred = tmpCompanyTitleError || tmpAddress1Error || tmpZipError || tmpFeinError;
    return {
      tmpCompanyTitleError,
      tmpAddress1Error,
      tmpZipError,
      tmpFeinError,
      validCompany: !anErrorOccurred,
    };
  }

  function validateUserFields() {
    const tmpFirstnameError = textIsInvalidUser('firstname');
    const tmpLastnameError = textIsInvalidUser('lastname');
    const tmpUsernameError = validateUsername(editUser?.username);
    const tmpPhoneError = phoneIsInvalid();
    const tmpPricingTierError = validatePricingTier(editCompany?.pricingProgram);
    const tmpProductError = validateProduct();

    setFirstnameError(tmpFirstnameError);
    setLastnameError(tmpLastnameError);
    setUsernameError(tmpUsernameError);
    setPhoneError(tmpPhoneError);
    setPricingTierError(tmpPricingTierError);
    setProductError(tmpProductError);

    let anErrorOccurred =
      tmpFirstnameError ||
      tmpLastnameError ||
      tmpUsernameError ||
      tmpPhoneError ||
      tmpPricingTierError ||
      tmpProductError;
    return {
      tmpFirstnameError,
      tmpLastnameError,
      tmpUsernameError,
      tmpPhoneError,
      tmpPricingTierError,
      tmpProductError,
      validUser: !anErrorOccurred,
    };
  }

  function validateUsername(value) {
    let usernameError = false;

    if (value === '' || value === ' ' || value === undefined || value === null) {
      usernameError = 'Cannot be empty';
    } else if (value.length >= 45) {
      usernameError = 'Email must be shorter than 45 characters';
    } else if (!email_helper.validateEmail(value)) {
      usernameError = 'Field must be in the format of name@example.com';
    }

    return usernameError;
  }

  function validatePricingTier(value) {
    let pricingTierError = false;
    if (!value || value === '' || value === 'none') {
      pricingTierError = 'Please select an available pricing tier';
    }
    return pricingTierError;
  }

  function validateProduct() {
    let productError = false;
    if (!enabledProducts || enabledProducts === '' || enabledProducts === 'none' || enabledProducts?.length === 0) {
      productError = 'Please select an available product';
    }
    return productError;
  }

  function phoneIsInvalid() {
    let value = editUser?.phone;
    let tmpPhoneError = false;
    if ((value === '' || value === ' ' || value === undefined || value === null) && isNew) {
      tmpPhoneError = 'Cannot be empty';
    } else if (value.length > 0 && value.length < 14) {
      tmpPhoneError = 'Field must be in the format of (123) 456-7890';
    }
    return tmpPhoneError;
  }

  /* eslint-disable  react-hooks/exhaustive-deps */
  useEffect(() => {
    if (enhancedRemoteQuickcheck) {
      setEditCompany({
        ...editCompany,
        functionalityAcls: [
          ...editCompany.functionalityAcls.filter(
            (acl) => acl.featureCode !== Constants.FUNCTIONALITY_ACL_FEATURE_CODE_ENHANCED_REMOTE_QUICK_CHECK
          ),
          { featureCode: Constants.FUNCTIONALITY_ACL_FEATURE_CODE_ENHANCED_REMOTE_QUICK_CHECK },
        ],
      });
    } else {
      setEditCompany({
        ...editCompany,
        functionalityAcls: [
          ...editCompany.functionalityAcls.filter(
            (acl) => acl.featureCode !== Constants.FUNCTIONALITY_ACL_FEATURE_CODE_ENHANCED_REMOTE_QUICK_CHECK
          ),
        ],
      });
    }
  }, [enhancedRemoteQuickcheck]);

  useEffect(() => {
    if (isNew && editCompany && !editCompany.isTest) {
      setEditCompany({ ...editCompany, trainingRequired: true, mfaRequired: true });
    }
    if (
      (!enabledProducts.includes('CASH_005') && editCompany.pricingProgram.toLowerCase().includes('cash')) ||
      !getPricingTierIntersection().includes(editCompany.pricingProgram)
    ) {
      setEditCompany({
        ...editCompany,
        pricingProgram: 'none',
      });
    }
  }, [enabledProducts, editCompany?.isTest]);
  /* eslint-enable  react-hooks/exhaustive-deps */

  const sanatizeId = (input) => input?.replace(/\s/g, '')?.replace(/\+/g, '') ?? '';

  const getPricingTierIntersection = () => {
    if (enabledProducts.length === 0) return [];

    return Object.keys(pricingProgramNames).filter((pp) => {
      if (enabledProducts.length === 1 && enabledProducts.includes('CASH_005')) {
        return pp.toLowerCase().includes('cash');
      }
      return enabledProducts
        ?.filter((product) => product !== 'CASH_005')
        ?.every((product) => pricingProgramNames[pp]?.includes(product));
    });
  };

  return (
    <div className="user-admin-details-page user-admin-company-info" id={'qa-company-details-edit'}>
      {isNew && (
        <>
          <Link
            id={'qa-back-to-company-info'}
            className="user-backlink"
            onClick={() => (view.coid ? goToCompanyList() : Actions.setRoute(`/account/companies`))}
            style={{ paddingTop: '10px' }}
          >
            <ArrowBackIcon /> Go Back to Company List
          </Link>

          <h1 id="company-title">{isNew ? 'Add Company' : 'Edit Company'}</h1>
        </>
      )}
      <form>
        <Box className={`layout-box`} style={{ height: 'auto' }}>
          <TextField
            required
            variant="outlined"
            color="primary"
            label="Company Name"
            name="name"
            id="name"
            className="input-field"
            value={unnullify(editCompany?.name)}
            error={!!companyTitleError}
            helperText={companyTitleError}
            onChange={(e) => {
              setEditCompany({
                ...editCompany,
                name: e.currentTarget.value,
              });
              setCompanyTitleError(false);
            }}
            onBlur={onBlurText}
          />

          <TextField
            required
            variant="outlined"
            color="primary"
            label="Address"
            name="address1"
            id="address1"
            className="input-field"
            value={unnullify(editCompany?.address1)}
            error={!!address1Error}
            helperText={address1Error}
            onChange={(e) => {
              setEditCompany({
                ...editCompany,
                address1: e.currentTarget.value,
              });
              setAddress1Error(false);
            }}
            onBlur={onBlurText}
          />
        </Box>

        <Box className={`layout-box  ${zipError && 'error-message-padding'}`}>
          <TextField
            disabled={true}
            variant="outlined"
            color="primary"
            label="City"
            name="city"
            id="city"
            className="input-field"
            value={unnullify(editCompany?.city)}
          />

          <TextField
            disabled={true}
            variant="outlined"
            color="primary"
            label="State"
            name="state"
            id="state"
            className="input-field"
            value={unnullify(editCompany?.state)}
          />

          <TextField
            required
            variant="outlined"
            color="primary"
            label="ZIP"
            name="zip"
            id="zip"
            className="input-field"
            value={unnullify(editCompany?.zip)}
            error={!!zipError}
            helperText={zipError}
            onChange={(e) => {
              if (e.currentTarget.value.length <= 5) {
                setEditCompany({
                  ...editCompany,
                  state: '',
                  city: '',
                  zip: e.currentTarget.value,
                });
                setZipError(false);
                setWaitingForChanges(true);
              }
            }}
            onBlur={async (e) => {
              const desiredZip = e.currentTarget.value;
              setIsLoading(true);
              onBlurText(e);
              if (desiredZip.length === 5) {
                const locationData = await Actions.fetchZip({ zip: desiredZip, setZipError });
                setEditCompany((previousState) => {
                  return {
                    ...previousState,
                    state: locationData?.state,
                    city: locationData?.city,
                  };
                });
              } else {
                setEditCompany((previousState) => {
                  return {
                    ...previousState,
                    state: '',
                    city: '',
                  };
                });
              }
              setIsLoading(false);
            }}
          />
        </Box>

        <Box className={`layout-box  ${(pricingTierError || productError) && 'error-message-padding'}`}>
          <FormControl style={{ display: 'flex', flexDirection: 'column', maxWidth: '50%' }} className="input-field">
            <InputLabel id="product-label">Product</InputLabel>
            <Select
              id="product"
              multiple
              value={enabledProducts}
              onChange={({ target: { value } }) => {
                const filteredList = value?.filter((selectedProduct) => {
                  const foundLenderConst = Constants.LENDERS.find((lender) => lender.productSku === selectedProduct);
                  return (
                    !foundLenderConst?.dependency || foundLenderConst?.dependency?.every((dep) => value.includes(dep))
                  );
                });
                return setEnabledProducts(filteredList);
              }}
              onBlur={onBlurProduct}
              input={<OutlinedInput label="Product" />}
              error={!!productError}
              disabled={(products?.length === 0 || !onCurrentProduct) && !isNew}
              renderValue={(selected) =>
                selected
                  ?.map((sku) => Constants.LENDERS?.find((lender) => lender.productSku === sku)?.label || sku)
                  .join(', ')
              }
            >
              {Constants.LENDERS?.filter(
                (lender) => !lender?.isTestOnly || lender?.isTestOnly === editCompany.isTest
              )?.map(({ productSku, label, dependency, parentProduct }) => {
                const disabled = dependency?.some((dep) => !enabledProducts?.includes(dep));
                return (
                  <MenuItem key={productSku} value={productSku} disabled={disabled}>
                    <Checkbox
                      checked={enabledProducts?.indexOf(productSku) > -1}
                      color="primary"
                      id={sanatizeId(`qa-menu-${label}`)}
                      disabled={disabled}
                    />
                    <ListItemText primary={label} />
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText className="helper-text-color">{productError}</FormHelperText>
          </FormControl>
          <TextField
            id="pricingTier"
            label="Pricing tier"
            className="input-field"
            value={unnullify(editCompany?.pricingProgram)}
            disabled={products?.length === 0 && !isNew}
            error={!!pricingTierError}
            helperText={pricingTierError}
            SelectProps={{
              native: true,
            }}
            InputLabelProps={{ shrink: true }}
            select
            variant="outlined"
            onChange={(e) => {
              setEditCompany({
                ...editCompany,
                pricingProgram: e.currentTarget.value,
              });
            }}
            onBlur={onBlurPricingTier}
            fullWidth
            required
          >
            <option value="none">Please Select a Pricing Tier...</option>
            {getPricingTierIntersection().map(
              (value, key) =>
                // only want to show QAA for tests, and not cash unless cash only
                (!isQaaPricingProgram(value) || editCompany?.isTest || editCompany?.pricingProgram === value) && (
                  <option {...{ key, value }}>{value.replace(/([a-z])([A-Z]|[0-9])/g, '$1 $2')}</option>
                )
            )}
          </TextField>
        </Box>
        {isCurrUserCPF && (
          <Box className={`layout-box ${feinError && 'error-message-padding'}`}>
            <div style={{ display: 'flex', flexDirection: 'column' }} className="input-field">
              {!isNew ? (
                <TextField
                  onChange={(e) => {
                    setEditCompany({
                      ...editCompany,
                      adminId: Number(e.currentTarget.value),
                    });
                  }}
                  defaultValue={companyInfo?.admin?.id}
                  label="Company Admin"
                  disabled={
                    !isCurrUserCPF ||
                    !FunctionalityAccessHelper.hasACL(currUserAcls, Constants.FUNCTIONALITY_ACL_EDIT_COMPANYADMIN)
                  }
                  SelectProps={{
                    native: true,
                  }}
                  select
                  variant="outlined"
                  id="qa-company-details-swap-admin"
                >
                  {companyInfo?.activeUsers?.map((user) => (
                    <option
                      {...{ key: user?.id, value: user?.id }}
                    >{`${user?.firstname} ${user?.lastname}(${user?.username})`}</option>
                  ))}
                </TextField>
              ) : (
                <div className="spacer" />
              )}
            </div>
            <TextField
              required
              variant="outlined"
              color="primary"
              label="EnsembleView Alpha ID"
              name="EnsembleView Alpha ID"
              id="fein"
              className="input-field"
              value={unnullify(editCompany?.fein)}
              error={!!feinError}
              helperText={feinError}
              onChange={(e) => {
                setEditCompany({
                  ...editCompany,
                  fein: e.currentTarget.value,
                });
                setFeinError(false);
              }}
              onBlur={onBlurText}
            />
          </Box>
        )}

        <CompanyElementForm
          elementsForm={elementsForm}
          setElementsForm={setElementsForm}
          startingVal={startingElementForm}
          hideSkus={companyElementsToHide}
        />

        <Box className="layout-box">
          <div style={{ display: 'flex', flexDirection: 'column' }} className="input-field">
            <div className="user-details-checkbox-container">
              <Checkbox
                id="edit-company-checkBox-aurora-lead-gen"
                checked={enhancedRemoteQuickcheck}
                onChange={() => {
                  setEnhancedRemoteQuickcheck(!enhancedRemoteQuickcheck);
                }}
                color="primary"
              />
              <span>Aurora Lead Gen</span>
            </div>
            <div className="user-details-checkbox-container">
              <Checkbox
                id="edit-company-checkBox-is-test"
                checked={editCompany?.isTest}
                onChange={() => {
                  let newCompany = { ...editCompany };
                  newCompany.isTest = !editCompany?.isTest;
                  if (isQaaPricingProgram(editCompany?.pricingProgram) && isNew) {
                    newCompany.pricingProgram = 'none';
                  }
                  setEditCompany(newCompany);
                }}
                color="primary"
              />
              <span>Test Company</span>
            </div>
            {editCompany?.isTest && (
              <div className="user-details-checkbox-container">
                <Checkbox
                  id="edit-company-checkBox-require-training"
                  checked={editCompany?.trainingRequired}
                  onChange={() =>
                    setEditCompany({
                      ...editCompany,
                      trainingRequired: !editCompany?.trainingRequired,
                    })
                  }
                  color="primary"
                />
                <span>Training Required</span>
              </div>
            )}
            <div className="user-details-checkbox-container">
              <Checkbox
                id="edit-company-checkBox-require-mfa"
                checked={editCompany?.mfaRequired}
                disabled={(companyInfo?.company?.mfaRequired && !isNew) || !editCompany?.isTest}
                onChange={() => setEditCompany({ ...editCompany, mfaRequired: !editCompany?.mfaRequired })}
                color="primary"
              />
              <span>MFA Required</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }} className="input-field">
            {products?.length > 0 && !onCurrentProduct && !isNew && (
              <Button
                color="primary"
                style={{ margin: '0 10px 0 auto', marginBottom: '10px', padding: '5px', height: '53.61px' }}
                variant="outlined"
                id="product-migrate-button"
                className="input-field"
                onClick={() =>
                  Actions.requestModals({
                    title: `Are you sure you want to Migrate this company to the ${migrationProduct?.label} product?`,
                    contents: (
                      <div>
                        {`Upon confirming this dialog, the company will be moved to the ${migrationProduct?.label} product. This action is `}
                        <b> irreversible</b>. Are you sure you wish to proceed?
                      </div>
                    ),
                    cancelButtonText: 'No',
                    okButtonText: 'Yes',
                    modalID: 'qa-company-migrate-toggle-modal',
                    okID: 'qa-company-migrate-toggle-confirm',
                    cancelID: 'qa-company-migrate-toggle-cancel',
                    okProcess: async () => {
                      // todo: migrate company is going to be broken
                      await Actions.migrateCompanyProduct([
                        { coid: companyInfo?.company?.coid, productSkus: [migrationProduct?.productSku] },
                      ]);
                      Actions.clearCompany();
                    },
                    cancelProcess: async () => {},
                  })
                }
              >
                <KeyboardDoubleArrowUpIcon /> {`Migrate Company to ${migrationProduct?.label}`}
              </Button>
            )}
          </div>
        </Box>
        {isNew && (
          <>
            <TitledDivider title="Admin User Details" />

            <Box
              className={`layout-box  ${(firstnameError || lastnameError) && 'error-message-padding'}`}
              style={{ marginTop: '2rem' }}
            >
              <TextField
                required
                variant="outlined"
                color="primary"
                label="First Name"
                name="firstname"
                id="firstname"
                className="input-field"
                value={unnullify(editUser?.firstname)}
                error={!!firstnameError}
                helperText={firstnameError}
                onChange={(e) => {
                  setEditUser({
                    ...editUser,
                    firstname: e.currentTarget.value,
                  });
                  setFirstnameError(false);
                }}
                onBlur={onBlurText}
              />

              <TextField
                required
                variant="outlined"
                color="primary"
                label="Last Name"
                name="lastname"
                id="lastname"
                className="input-field"
                error={!!lastnameError}
                helperText={lastnameError}
                value={unnullify(editUser?.lastname)}
                onChange={(e) => {
                  setEditUser({
                    ...editUser,
                    lastname: e.currentTarget.value,
                  });
                  setLastnameError(false);
                }}
                onBlur={onBlurText}
              />
            </Box>

            <Box className={`layout-box  ${(usernameError || phoneError) && 'double-error-message-padding'}`}>
              <TextField
                required
                variant="outlined"
                color="primary"
                label="Email"
                name="username"
                id="username"
                className="input-field"
                error={!!usernameError}
                helperText={usernameError}
                value={unnullify(editUser?.username)}
                onChange={(e) => {
                  setEditUser({
                    ...editUser,
                    username: e.currentTarget.value,
                  });
                  setUsernameError(false);
                }}
                onBlur={onBlurUsername}
              />
              <div className="input-field">
                <TextField
                  required
                  variant="outlined"
                  color="primary"
                  label="Phone"
                  name="phone"
                  id="phone"
                  className="input-field-phone"
                  error={!!phoneError}
                  helperText={phoneError}
                  value={unnullify(editUser?.phone)}
                  onChange={(e) => {
                    setEditUser({
                      ...editUser,
                      phone: e.currentTarget.value,
                    });
                    setPhoneError(false);
                  }}
                  InputLabelProps={{ shrink: !!editUser?.phone }}
                  InputProps={{
                    inputComponent: PhoneFormatInput,
                  }}
                  onBlur={onBlurPhone}
                />
                <div className="spacer-slim" />
                <TextField
                  variant="outlined"
                  color="primary"
                  label="ext."
                  name="extension"
                  id="phoneExtension"
                  value={unnullify(editUser?.extension)}
                  onChange={(e) => {
                    setEditUser({
                      ...editUser,
                      extension: e.currentTarget.value,
                    });
                  }}
                  InputLabelProps={{ shrink: editUser?.extension }}
                  InputProps={{
                    inputComponent: ExtensionFormatInput,
                  }}
                />
              </div>
            </Box>
          </>
        )}
        <Box display="flex" justifyContent="flex-end" style={{ marginTop: '20px' }}>
          {(isNew || changeDialogContext?.isChanges) && (
            <Button
              disabled={isLoading}
              onClick={() => (isNew ? Actions.setRoute(`/account/companies`) : setIsCancelDialogOpen(true))}
              id="qa-admin-company-details-save-cancel"
              style={{ color: 'black' }}
            >
              Cancel
            </Button>
          )}
          <Button
            disabled={
              isLoading ||
              (!changeDialogContext?.isChanges && !isNew) ||
              waitingForChanges ||
              enabledProducts.length === 0 ||
              editCompany.pricingProgram === 'none'
            }
            variant="contained"
            color="primary"
            id="qa-admin-company-details-save-button"
            onClick={onSave}
          >
            {isLoading ? (
              <div className={'processing-icon load-spin spin'} style={{ color: 'white' }}>
                <Sun className="sun-icon icon" /> Loading
              </div>
            ) : isNew ? (
              'Add Company'
            ) : (
              'Save'
            )}
          </Button>
        </Box>
      </form>
      <DialogBox open={!!changeDialogContext?.displayDialog} onAgree={() => onSave()} onCancel={() => onCancel()} />
      <CancelDialogBox
        open={isCancelDialogOpen}
        onAgree={() => {
          setEnhancedRemoteQuickcheck(
            initialSetOfFunctionalityAcls?.some(
              (element) => element?.featureCode === Constants.FUNCTIONALITY_ACL_FEATURE_CODE_ENHANCED_REMOTE_QUICK_CHECK
            )
          );
          setEditCompany(companyInfo?.company);
          setIsCancelDialogOpen(false);
          setCompanyTitleError(false);
          setAddress1Error(false);
          setZipError(false);
          setFeinError(false);
        }}
        onCancel={() => setIsCancelDialogOpen(false)}
      />
    </div>
  );
};

const ConnectedEditCompanyWithFormWithData = withDataLoaded({
  WithData: EditCompany,
  WithoutData: PageSpinner,
  data: [userLoader, companyDetailsLoader, pricingProgramNamesLoader, companyElementTypesLoader],
});

export default ConnectedEditCompanyWithFormWithData;
